import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Download, Save, Pencil, Loader2, Sparkles, Film, X, Check } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const ANIM_COST = { "15-480p": 125, "15-720p": 200, "30-480p": 250, "30-720p": 400 };
const animCost = (d, q) => ANIM_COST[`${d}-${q}`] || 200;

const EDIT_EXAMPLES = [
  "Remove all dialogue boxes from the comic except panels 3 and 5. Panel 3 should only say “hmm?” and panel 5 should only say “?!”. In panel 6 keep the scene and character the same, but change the laptop display to a green screen. In panels 7 and 8 remove the characters completely and leave only a clean green-screen background. Everything else stays unchanged.",
  "Panel 1: Replace with an opening drone shot of the city shown in the reference image. Panel 3: Use what was originally panel 6. Panel 5: Use what was originally panel 2. Panel 6: Use what was originally panel 3, but green-screened. Panel 8: Make the entire panel a full green screen.",
];

function EditModal({ open, onClose, project, onDone }) {
  const [instr, setInstr] = useState("");
  const [busy, setBusy] = useState(false);
  const [showEx, setShowEx] = useState(false);
  useEffect(() => { if (open) { setInstr(""); setShowEx(false); } }, [open]);
  if (!open) return null;

  const src = project.panel_image_base64 ? `data:image/png;base64,${project.panel_image_base64}` : project.panel_image_url;

  const regen = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/astro/generate`, {
        project_id: project.id, edit_instructions: instr, origin_url: window.location.origin,
      });
      onDone(data);
      onClose();
      toast.success("Panels updated");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Regeneration failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[85] flex items-end sm:items-center justify-center" data-testid="astro-edit-modal">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full sm:max-w-md bg-ink-900 border-t sm:border border-zinc-800 sm:rounded-2xl p-5 max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading font-bold text-lg">Edit Panels</h3>
          <button onClick={onClose} className="text-zinc-500 hover:text-white"><X size={20} /></button>
        </div>
        <div className="flex gap-3 mb-3">
          <img src={src} alt="latest" className="w-20 h-20 object-cover rounded-lg border border-zinc-800" />
          <p className="text-xs text-zinc-500 flex-1">The latest panel image becomes the reference. Your instructions modify this version — the project isn't restarted from scratch.</p>
        </div>
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-xs text-zinc-400">Editing instructions</span>
          <button onClick={() => setShowEx(true)} data-testid="astro-edit-examples" className="text-xs text-gold hover:underline">Editing Examples</button>
        </div>
        <textarea value={instr} onChange={(e) => setInstr(e.target.value)} data-testid="astro-edit-input" rows={5}
          placeholder="Describe the changes you want (e.g. In panel 6, change the laptop display to a green screen)…"
          className="w-full bg-ink-950 border border-zinc-800 rounded-lg px-3 py-2.5 text-sm focus:border-gold focus:outline-none resize-none" />
        <button onClick={regen} disabled={busy} data-testid="astro-edit-regen"
          className="mt-3 w-full flex items-center justify-center gap-2 rounded-lg bg-gold text-ink-950 font-heading font-black text-lg py-3 hover:brightness-105 disabled:opacity-60">
          {busy ? <><Loader2 className="animate-spin" size={18} /> Astro-ing…</> : <><Sparkles size={18} /> Astro It</>}
        </button>
        <p className="text-center text-[11px] text-zinc-600 mt-2">Regenerates the {project.panel_count || 4}-panel comic · 15 credits</p>

        {showEx && (
          <div className="fixed inset-0 z-[95] flex items-center justify-center p-4" data-testid="astro-edit-examples-popup">
            <div className="absolute inset-0 bg-black/80" onClick={() => setShowEx(false)} />
            <div className="relative w-full max-w-md bg-ink-900 border border-zinc-800 rounded-2xl p-5 max-h-[80vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-heading font-bold text-lg">Editing Examples</h3>
                <button onClick={() => setShowEx(false)} className="text-zinc-500 hover:text-white"><X size={20} /></button>
              </div>
              <ul className="space-y-3">
                {EDIT_EXAMPLES.map((e, i) => (
                  <li key={i} className="text-sm text-zinc-300 border border-zinc-800 rounded-lg p-3 bg-ink-950/40"><span className="text-gold font-heading font-bold">Example {i + 1}</span><p className="mt-1 leading-relaxed">{e}</p></li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function AstroStudio() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [project, setProject] = useState(null);
  const [prompt, setPrompt] = useState("");
  const [genPrompt, setGenPrompt] = useState(false);
  const [animating, setAnimating] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const [title, setTitle] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/projects/${projectId}`);
      setProject(data);
      setTitle(data.title || "");
      setPrompt(data.animation_prompt || "");
    } catch { toast.error("Project not found"); navigate("/creations"); }
  }, [projectId, navigate]);

  useEffect(() => { load(); }, [load]);

  const onEdited = (data) => { setProject(data); refreshUser(); };

  const saveTitle = async () => {
    setEditingTitle(false);
    if (title.trim() && title !== project.title) {
      try { const { data } = await api.put(`/astro/${projectId}`, { title: title.trim() }); setProject(data); }
      catch { toast.error("Couldn't save title"); }
    }
  };

  const generatePrompt = async () => {
    setGenPrompt(true);
    try {
      const { data } = await api.post(`/astro/${projectId}/generate-prompt`);
      setPrompt(data.animation_prompt);
      toast.success("Animation prompt ready");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Prompt generation failed"); }
    finally { setGenPrompt(false); }
  };

  const doSave = async () => {
    setSaving(true);
    try {
      await api.put(`/astro/${projectId}`, { animation_prompt: prompt });
      await api.post(`/astro/${projectId}/save`);
      toast.success("Saved to Creations");
      load();
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const download = () => {
    if (!project?.panel_image_base64 && !project?.panel_image_url) return;
    const a = document.createElement("a");
    a.href = project.panel_image_base64 ? `data:image/png;base64,${project.panel_image_base64}` : project.panel_image_url;
    a.download = `${(project.title || "astro").replace(/\s+/g, "_")}.png`;
    document.body.appendChild(a); a.click(); a.remove();
  };

  const generateAnimation = async () => {
    if (!prompt.trim()) { toast.error("Write or generate an animation prompt first"); return; }
    setAnimating(true);
    try {
      await api.put(`/astro/${projectId}`, { animation_prompt: prompt });
      await api.post(`/astro/${projectId}/animate`, { prompt, origin_url: window.location.origin });
      await refreshUser();
      toast.success("Animation started 🎬");
      navigate("/creations");
    } catch (e) {
      if (e.response?.status === 402) { toast.error("Not enough credits."); navigate("/pricing"); }
      else toast.error(formatApiError(e.response?.data?.detail) || "Animation failed");
      setAnimating(false);
    }
  };

  if (!project) {
    return <div className="min-h-screen flex items-center justify-center bg-ink-950 text-white"><Loader2 className="animate-spin text-gold" size={28} /></div>;
  }

  const src = project.panel_image_base64 ? `data:image/png;base64,${project.panel_image_base64}` : project.panel_image_url;
  const cost = animCost(project.params?.duration || 15, project.params?.quality || "720p");

  return (
    <div className="min-h-screen bg-ink-950 text-[#F0E9DD] pb-28">
      {/* header */}
      <header className="sticky top-0 z-40 bg-ink-950/80 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-3xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/creations")} data-testid="astro-back" className="text-zinc-400 hover:text-white"><ArrowLeft size={22} /></button>
            <h1 className="font-heading font-bold text-xl tracking-wide"><span className="text-gold">ASTRO</span> STUDIO</h1>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={download} data-testid="astro-download" className="w-10 h-10 rounded-lg border border-zinc-700 flex items-center justify-center text-zinc-300 hover:border-gold hover:text-gold transition-colors"><Download size={18} /></button>
            <button onClick={doSave} disabled={saving} data-testid="astro-save" className={`w-10 h-10 rounded-lg border flex items-center justify-center transition-colors ${project.saved ? "border-gold text-gold bg-[#1a140d]" : "border-zinc-700 text-zinc-300 hover:border-gold hover:text-gold"}`}>
              {saving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 pt-4 space-y-4">
        {/* title + panels image */}
        <div className="border border-zinc-800 rounded-2xl overflow-hidden bg-ink-900">
          <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800">
            {editingTitle ? (
              <input autoFocus value={title} onChange={(e) => setTitle(e.target.value)} onBlur={saveTitle}
                onKeyDown={(e) => e.key === "Enter" && saveTitle()} data-testid="astro-title-input"
                className="flex-1 bg-ink-950 border border-zinc-700 rounded px-2 py-1 text-base font-heading font-bold focus:outline-none focus:border-gold" />
            ) : (
              <h2 className="font-heading font-bold text-lg truncate" data-testid="astro-title">{project.title}</h2>
            )}
            <button onClick={() => (editingTitle ? saveTitle() : setEditingTitle(true))} data-testid="astro-title-edit" className="ml-3 flex items-center gap-1 text-zinc-400 hover:text-gold text-sm">
              {editingTitle ? <Check size={16} /> : <>Panels <Pencil size={14} /></>}
            </button>
          </div>
          <div className="relative">
            {src ? <img src={src} alt={project.title} className="w-full object-contain bg-black" data-testid="astro-panel-image" />
              : <div className="aspect-[3/4] flex items-center justify-center text-zinc-700"><Film size={40} /></div>}
            <button onClick={() => setEditOpen(true)} data-testid="astro-edit-btn"
              className="absolute top-3 right-3 flex items-center gap-1.5 bg-ink-950/80 border border-zinc-700 rounded-full px-3 py-1.5 text-sm text-gold hover:border-gold">
              <Pencil size={14} /> Edit
            </button>
          </div>
        </div>

        {/* generate prompt */}
        <button onClick={generatePrompt} disabled={genPrompt} data-testid="astro-generate-prompt"
          className="w-full flex items-center justify-center gap-2 rounded-lg bg-gradient-to-b from-goldlight to-gold text-ink-950 font-heading font-black text-lg py-3 hover:brightness-105 disabled:opacity-60">
          {genPrompt ? <><Loader2 className="animate-spin" size={20} /> Analysing panels…</> : <><Sparkles size={18} /> Generate Prompt</>}
        </button>

        {/* prompt box */}
        <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} data-testid="astro-prompt-input" rows={8}
          placeholder="Describe your animation — time of day, emotions, character descriptions, camera angles, weather, dialogue. Or tap Generate Prompt to write it for you."
          className="w-full bg-ink-900 border border-gold/40 rounded-xl px-4 py-3 text-sm leading-relaxed focus:border-gold focus:outline-none resize-none placeholder:text-zinc-600" />

        {/* generate animation */}
        <button onClick={generateAnimation} disabled={animating} data-testid="astro-generate-animation"
          className="w-full flex items-center justify-center gap-2 rounded-lg bg-[#1a140d] border border-gold/60 text-gold font-heading font-black text-lg py-3.5 hover:bg-[#241a10] transition-colors disabled:opacity-60">
          {animating ? <><Loader2 className="animate-spin" size={20} /> Starting…</> : <><Film size={18} /> Generate Animation · {cost} credits</>}
        </button>
        <p className="text-center text-[11px] text-zinc-600">{project.params?.duration || 15}s · {project.panel_count || 4} panels · {project.params?.quality || "720p"} · {project.params?.ratio || "9:16"}</p>
      </main>

      <EditModal open={editOpen} onClose={() => setEditOpen(false)} project={project} onDone={onEdited} />
    </div>
  );
}
